import { createAsyncActions } from './createAsyncActions';
import createFetchDataAction from './createFetchDataAction';
import createSlice from './createSlice';
import { createSyncActions } from './createSyncActions';
import type { Controller, ControllerConfig } from './types';

export type { AsyncActionDefinition, FetchDataParams } from './types';

export default function createController<TState>(
  input: ControllerConfig<TState>,
): Controller<TState> {
  const fetchDataThunkActionDefinition = createFetchDataAction(input);

  const asyncActions = createAsyncActions(input.name, input.asyncActions || {});

  const syncActions = createSyncActions(input.name, input.actions || {});

  const socketActions = Object.keys(input?.socketListeners || {}).reduce<
    Record<string, NonNullable<typeof input.socketListeners>[string]>
  >((acc, name) => {
    return {
      ...acc,
      [name]: input.socketListeners![name],
    };
  }, {});

  const socketListenerActions = createAsyncActions(input.name, socketActions);

  const allAsyncThunkActionDefinitions = [
    fetchDataThunkActionDefinition,
    ...syncActions.asyncThunkActionDefinitions,
    ...asyncActions.asyncThunkActionDefinitions,
    ...socketListenerActions.asyncThunkActionDefinitions,
  ];

  const slice = createSlice(input, allAsyncThunkActionDefinitions);

  return {
    name: input.name,
    fetchData: fetchDataThunkActionDefinition.asyncThunk,
    actions: {
      ...syncActions.asyncActions,
      ...asyncActions.asyncActions,
    },
    socketListeners: socketListenerActions.asyncActions,
    pageActions: slice.actions as Controller<TState>['pageActions'],
    reducer: slice.reducer,
  };
}
